import { ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NewButton from "../../../components/Atomos/Buttons/newButton";
import { getManageSecondmentEditor, ManageSecondmentActor, ManageSecondmentDraft, saveManageSecondment } from "../../../Services/ManageSecondments";
import { SecondmentChoices, SecondmentNarrative, SecondmentSchedule } from "./ManageSecondmentsFields";

interface ManageSecondmentsFormProps {
  actor: ManageSecondmentActor;
  mode: "add" | "update";
  secId: string;
}

export default function ManageSecondmentsForm({ actor, mode, secId }: ManageSecondmentsFormProps): ReactElement {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<ManageSecondmentDraft | null>(null);
  const [missing, setMissing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [editorReady, setEditorReady] = useState(false);
  const [catalog, setCatalog] = useState<{ contacts: { id: number; name: string }[]; locations: { id: number; name: string }[]; practices: { id: number; name: string }[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getManageSecondmentEditor(actor, mode === "update" ? secId : "")
      .then((editor) => {
        if (cancelled) {
          return;
        }
        setEditorReady(true);
        setCatalog({ contacts: editor.contacts, locations: editor.locations, practices: editor.practices });
        if (!editor.draft) {
          setMissing(true);
          return;
        }
        setDraft(editor.draft);
      })
      .catch(() => {
        if (!cancelled) {
          setEditorReady(true);
          setErrors(["The secondment editor could not be loaded."]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [actor, mode, secId]);

  function patch(next: Partial<ManageSecondmentDraft>): void {
    setDraft((current) => (current ? { ...current, ...next } : current));
  }

  function toggle(ids: number[], id: number, checked: boolean): number[] {
    if (checked) {
      return [...ids, id];
    }
    return ids.filter((value) => value !== id);
  }

  async function submit(): Promise<void> {
    if (!draft) {
      return;
    }
    const result = await saveManageSecondment(actor, {
      mode,
      secId: mode === "update" ? Number(secId) : null,
      secContactId: draft.contactId,
      openDeadline: draft.openDeadline,
      deadline: draft.deadline,
      openPosting: draft.openPosting,
      dateFrom: draft.dateFrom,
      dateTo: draft.dateTo,
      title: draft.title,
      duration: draft.duration,
      locationIds: draft.locationIds,
      allLocations: draft.allLocations,
      description: draft.description,
      practiceIds: draft.practiceIds,
      allPractices: draft.allPractices,
      noRequirements: draft.noRequirements,
      requirements: draft.requirements,
      additionalInfo: draft.additionalInfo,
      overview: draft.overview,
    });
    if (!result.saved) {
      setErrors(result.errors);
      return;
    }
    navigate("/manage_secondments");
  }

  if (!editorReady || !catalog) {
    return <p className="form-status">{errors[0] || "Loading the secondment editor."}</p>;
  }
  if (missing || !draft) {
    return <p className="form-status">That secondment is not on your firm list.</p>;
  }
  const editor = { contacts: catalog.contacts, locations: catalog.locations, practices: catalog.practices, draft, overview: draft.overview };
  return (
    <div className="stack">
      <h2>{mode === "add" ? "Submit Secondment Opportunity" : "Edit Secondment"}</h2>
      <p>Submit secondment listings that encompass the entire programs or individual listings that identify opportunities by length of stay, areas of practice, locations, professional age and experience, and any other filter used to differentiate within the secondment programs offered by the firm.</p>
      <NewButton text={actor.isFirmAdmin ? "Manage Firm Secondments" : "Manage Secondments"} onClick={() => navigate("/manage_secondments")} />
      {errors.length > 0 ? (
        <div className="framed-block">
          <p className="form-status">Please correct the following error(s)</p>
          {errors.map((error) => <p key={error} className="form-status">{error}</p>)}
        </div>
      ) : null}
      <p className="caption">All fields marked with * are required</p>
      <p>Because the secondment listing database can be searched by keywords, jurisdiction or practice industry, we encourage the information to be entered as text to optimize the search capabilities and specifically identify the secondment opportunities being realized.</p>
      <SecondmentNarrative
        overview={draft.overview}
        description={draft.description}
        noRequirements={draft.noRequirements}
        requirements={draft.requirements}
        additionalInfo={draft.additionalInfo}
        onOverview={(value) => patch({ overview: value })}
        onDescription={(value) => patch({ description: value })}
        onNoRequirements={(value) => patch({ noRequirements: value, requirements: value ? "" : draft.requirements })}
        onRequirements={(value) => patch({ requirements: value })}
        onAdditional={(value) => patch({ additionalInfo: value })}
      />
      <SecondmentSchedule
        editor={editor}
        contactId={draft.contactId}
        openDeadline={draft.openDeadline}
        deadline={draft.deadline}
        openPosting={draft.openPosting}
        dateFrom={draft.dateFrom}
        dateTo={draft.dateTo}
        title={draft.title}
        duration={draft.duration}
        onContact={(value) => patch({ contactId: value })}
        onOpenDeadline={(value) => patch({ openDeadline: value, deadline: value ? "" : draft.deadline })}
        onDeadline={(value) => patch({ deadline: value })}
        onOpenPosting={(value) => patch({ openPosting: value, dateFrom: value ? "" : draft.dateFrom, dateTo: value ? "" : draft.dateTo })}
        onDateFrom={(value) => patch({ dateFrom: value })}
        onDateTo={(value) => patch({ dateTo: value })}
        onTitle={(value) => patch({ title: value })}
        onDuration={(value) => patch({ duration: value })}
      />
      <SecondmentChoices
        label="Secondment Location"
        options={catalog.locations}
        selected={draft.locationIds}
        allSelected={draft.allLocations}
        showAll={catalog.locations.length > 1}
        onToggle={(id, checked) => patch({ locationIds: toggle(draft.locationIds, id, checked), allLocations: false })}
        onAll={(checked) => patch({ allLocations: checked, locationIds: checked ? catalog.locations.map((row) => row.id) : [] })}
      />
      <SecondmentChoices
        label="Practice Areas"
        options={catalog.practices}
        selected={draft.practiceIds}
        allSelected={draft.allPractices}
        showAll={catalog.practices.length > 1}
        onToggle={(id, checked) => patch({ practiceIds: toggle(draft.practiceIds, id, checked), allPractices: false })}
        onAll={(checked) => patch({ allPractices: checked, practiceIds: checked ? catalog.practices.map((row) => row.id) : [] })}
      />
      <div className="flex flex-wrap gap-4">
        <NewButton text="Submit" onClick={() => { void submit(); }} />
        <NewButton text="Cancel" color="neutral" onClick={() => navigate("/manage_secondments")} />
      </div>
    </div>
  );
}
